"use client";

import { useMemo } from "react";
import {
  KEYBOARD_VIEWBOX_HEIGHT,
  MiniPiano,
  keyCenterX,
  keyboardViewBoxWidth,
} from "@/components/ui/molecules/mini-piano";
import { useActivePianoNotes } from "@/hooks/use-active-piano-notes";
import type { Phrase } from "@/lib/learning-journey/types";
import { fingerMark, gripKeyboardSize } from "../grip";
import type { KeyboardWindow } from "../phrase-chords";
import {
  fingersAt,
  type HeldKey,
  type PracticeGroup,
  type PracticeHand,
} from "../practice";
import {
  ARC_Y,
  FINGER_Y,
  FingerPill,
  HandArc,
  KeyFoot,
  OutOfWindowChevron,
  regionTint,
  StartMark,
} from "./keyboard-marks";
import { KEYBOARD_H_PX, KEYBOARD_MAX_W_PX } from "./panel-metrics";

/** Half-length of the dash marking a hand that holds a single key. */
const SINGLE_KEY_DASH = 4;

/**
 * The keys of the current group, on the same keyboard S0 and S1a drew.
 *
 * This replaced a row of lettered tiles. The tiles named the keys and said
 * nothing about where they are, which broke the one thing the player had
 * just spent a whole screen building — a spatial map of the hand on a
 * drawing of a keyboard — and it broke it at the step whose subject is
 * precisely the hand *moving* on that map. A grid of squares that swaps its
 * contents shows no movement; an eye following a lit key across a keyboard
 * sees nothing else.
 *
 * A key that is down takes the full hand colour, a key still to take is
 * tinted: the same two-state paint the grip step uses, so "I am holding this
 * one" reads identically on both screens.
 */
export function PracticeKeyboard({
  group,
  phrase,
  keyboardWindow,
  landmarks,
  held,
  flash,
  dim,
  arcY = () => ARC_Y,
  markSingleKey = false,
  startsOn,
}: {
  group: PracticeGroup;
  phrase: Phrase;
  keyboardWindow: KeyboardWindow;
  landmarks: ReadonlyMap<number, string>;
  held: readonly HeldKey[];
  flash: boolean;
  dim: boolean;
  /**
   * Keys the pass starts on, marked with a caret.
   *
   * The map of a unit lights every key it uses, undifferentiated, while the
   * screen says the first note launches the count-in — a sentence about a
   * note nothing on the panel pointed at. The roll shows it, and the roll is
   * behind the panel half the time.
   */
  readonly startsOn?: readonly number[];
  /** Where each hand's arc is drawn. One shared lane unless told otherwise. */
  arcY?: (hand: PracticeHand) => number;
  /** Draw a short dash for a hand holding a single key, so its lane is never blank. */
  markSingleKey?: boolean;
}) {
  const pressed = useActivePianoNotes();
  const { lowMidi, octaves } = keyboardWindow;
  const viewWidth = keyboardViewBoxWidth(octaves);
  const { widthPx, heightPx } = gripKeyboardSize(
    viewWidth,
    KEYBOARD_VIEWBOX_HEIGHT,
    KEYBOARD_H_PX,
    KEYBOARD_MAX_W_PX,
  );

  const midis = group.notes.map((n) => n.midi);

  const midiColors = useMemo(() => {
    const map = new Map<number, string>();

    for (const note of group.notes) {
      const color = handColorOf(note.hand);
      map.set(
        note.midi,
        pressed.has(note.midi) ? color : regionTint(note.midi, color),
      );
    }

    return map;
  }, [group, pressed]);

  // One arc per hand.
  //
  // A hand whose keys run past the window used to lose its arc entirely,
  // silently: the drawing is capped at four octaves, so a key of the group
  // simply was not there and nothing said so. The arc now runs to the edge
  // and a chevron says the rest is out of frame — never a line drawn across
  // the survivors, which would assert a hand narrower than the one asked for.
  const highest = lowMidi + octaves * 12;
  const arcs = (["L", "R"] as const)
    .map((hand) => {
      const keys = group.notes.filter((n) => n.hand === hand);

      if (keys.length === 0 || (keys.length === 1 && !markSingleKey)) {
        return null;
      }

      const placed = keys.map((n) => ({
        midi: n.midi,
        x: keyCenterX(n.midi, lowMidi, octaves),
      }));
      const inside = placed
        .map((k) => k.x)
        .filter((x): x is number => x !== null);
      const clippedLow = placed.some((k) => k.x === null && k.midi < lowMidi);
      const clippedHigh = placed.some((k) => k.x === null && k.midi >= highest);

      if (inside.length === 0) {
        return null;
      }

      let x1 = Math.min(...inside);
      let x2 = Math.max(...inside);

      if (x1 === x2 && !clippedLow && !clippedHigh) {
        x1 -= SINGLE_KEY_DASH;
        x2 += SINGLE_KEY_DASH;
      }

      if (clippedLow) {
        x1 = 0;
      }

      if (clippedHigh) {
        x2 = viewWidth;
      }

      return { hand, x1, x2, clippedLow, clippedHigh };
    })
    .filter((a) => a !== null);

  const heldMarks = held
    .map((k) => ({ ...k, x: keyCenterX(k.midi, lowMidi, octaves) }))
    .filter((k) => k.x !== null);

  const startMarks = (startsOn ?? [])
    .map((midi) => ({ midi, x: keyCenterX(midi, lowMidi, octaves) }))
    .filter((k) => k.x !== null);

  const fingerLabels = group.notes
    .map((note) => ({
      midi: note.midi,
      x: keyCenterX(note.midi, lowMidi, octaves),
      mark: fingerMark(
        fingersAt(
          phrase.gripsByHand[note.hand === "L" ? "left" : "right"],
          group.startTick,
          note.midi,
        ),
      ),
    }))
    .filter((f) => f.x !== null && f.mark !== "");

  return (
    <div
      style={{
        position: "relative",
        width: widthPx,
        height: heightPx,
        margin: "4px auto 14px",
        opacity: dim ? 0.55 : 1,
        transition: "opacity 200ms ease-out",
      }}
    >
      <MiniPiano
        midis={midis}
        lowMidi={lowMidi}
        octaves={octaves}
        midiColors={midiColors}
        landmarks={landmarks}
        showLabels
        style={{ width: "100%", height: "100%" }}
      />

      <svg
        viewBox={`0 0 ${viewWidth} ${KEYBOARD_VIEWBOX_HEIGHT}`}
        preserveAspectRatio="xMidYMid meet"
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
        }}
      >
        {arcs.map((arc) => (
          <g key={arc.hand}>
            <HandArc
              x1={arc.x1}
              x2={arc.x2}
              y={arcY(arc.hand)}
              color={handColorOf(arc.hand)}
              opacity={0.75}
            />
            {arc.clippedLow && (
              <OutOfWindowChevron
                side="low"
                viewWidth={viewWidth}
                y={arcY(arc.hand)}
                color={handColorOf(arc.hand)}
              />
            )}
            {arc.clippedHigh && (
              <OutOfWindowChevron
                side="high"
                viewWidth={viewWidth}
                y={arcY(arc.hand)}
                color={handColorOf(arc.hand)}
              />
            )}
          </g>
        ))}

        {startMarks.map((k) => (
          <StartMark key={`start-${k.midi}`} x={k.x ?? 0} />
        ))}

        {heldMarks.map((k) => (
          <KeyFoot
            key={`held-${k.midi}`}
            x={k.x ?? 0}
            midi={k.midi}
            color={handColorOf(k.hand)}
          />
        ))}

        {fingerLabels.map((f) => (
          // Raised off the baseline: unlike the grip step, this keyboard also
          // writes the note name on a lit key, and the two would sit on top
          // of each other at the default height.
          <FingerPill
            key={`finger-${f.midi}`}
            x={f.x ?? 0}
            mark={f.mark}
            y={FINGER_Y - 10}
          />
        ))}
      </svg>

      {flash && (
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: -6,
            borderRadius: 10,
            boxShadow:
              "0 0 24px rgba(255,255,255,0.55), 0 0 48px rgba(255,255,255,0.25)",
            transition: "box-shadow 160ms ease-out",
            pointerEvents: "none",
          }}
        />
      )}
    </div>
  );
}

export function handColorOf(hand: PracticeHand): string {
  return hand === "L" ? "var(--color-hand-left)" : "var(--color-hand-right)";
}

